import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Mail, ChevronLeft, Sparkles, BadgeCheck, ShieldCheck, Building2,
  KeyRound, Star, ArrowRight, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, portalFor } from "@/lib/auth";

const HERO = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1800&q=80";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("choose"); // choose | buyer | staff
  const [mobile, setMobile] = useState("9876543210");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [email, setEmail] = useState("karthiga@radiancerealty.in");
  const [password, setPassword] = useState("demo123");
  const [busy, setBusy] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (otpSent && otpRefs.current[0]) otpRefs.current[0].focus();
  }, [otpSent]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      await api.post("/auth/buyer/otp/request", { mobile });
      setOtpSent(true);
      toast.success("OTP sent · demo code is 1234");
    } catch { toast.error("Failed to send OTP"); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 4) { toast.error("Enter 4-digit OTP"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/buyer/otp/verify", { mobile, otp: code });
      login(data.token, data.user);
      nav(portalFor(data.user.role));
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
    finally { setBusy(false); }
  };
  const staffLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/staff/login", { email, password });
      login(data.token, data.user);
      nav(portalFor(data.user.role));
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid credentials"); }
    finally { setBusy(false); }
  };

  const setOtpAt = (i, v) => {
    const next = [...otp];
    next[i] = v.replace(/\D/g, "").slice(0, 1);
    setOtp(next);
    if (next[i] && i < 3) otpRefs.current[i + 1]?.focus();
    if (next[i] && i === 3 && next.every(Boolean)) setTimeout(() => verifyOtp(), 80);
  };
  const onOtpKey = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  const fillStaffPreset = (em) => { setEmail(em); setPassword("demo123"); };

  return (
    <div className="min-h-screen flex flex-col md:flex-row text-white relative overflow-hidden" style={{ background: "#0A1628" }}>
      {/* Decorative aurora */}
      <div className="rc-aurora" aria-hidden/>

      {/* LEFT: Hero panel */}
      <div className="relative md:w-[58%] min-h-[50vh] md:min-h-screen overflow-hidden">
        <div className="rc-hero-img rc-kenburns" style={{ backgroundImage: `url(${HERO})` }} aria-hidden/>
        <div className="rc-hero-overlay" aria-hidden/>
        <div className="relative h-full flex flex-col justify-between p-6 md:p-12 z-10" style={{ textShadow: "0 1px 3px rgba(0, 0, 0, 0.5)" }}>
          {/* top brand */}
          <div className="flex items-center gap-3 rc-rise">
            <img src="/radiance-logo.jpeg" alt="" className="w-12 h-12 rounded-xl object-cover ring-2 ring-white/20"/>
            <div>
              <div className="text-xl font-bold tracking-tight">Radiance Connect</div>
              <div className="text-[12px] text-white/70 uppercase tracking-[2px]">By Radiance Realty · Since 1948</div>
            </div>
          </div>

          {/* middle pitch */}
          <div className="max-w-xl space-y-6 my-8 md:my-0">
            <div className="rc-tag rc-rise" style={{ animationDelay: "120ms" }}>
              <Sparkles size={12}/> 77-year legacy · Trust in every brick
            </div>
            <h1 className="rc-large-title md:text-6xl leading-[1.05] rc-rise" style={{ animationDelay: "220ms", fontSize: "clamp(34px, 5vw, 64px)", fontWeight: 700, letterSpacing: "-1.4px" }}>
              Your home journey,<br/>
              <span className="rc-gradient-text">beautifully orchestrated.</span>
            </h1>
            <p className="rc-body text-white/80 max-w-md rc-rise" style={{ animationDelay: "320ms", fontSize: 18, lineHeight: 1.55 }}>
              From booking to handover — track milestones, payments, documents and on-site progress in one premium destination.
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2 rc-rise" style={{ animationDelay: "420ms" }}>
              {[
                { i: Building2,  t: "Live construction" },
                { i: KeyRound,   t: "Handover ready" },
                { i: ShieldCheck,t: "RERA-compliant" },
                { i: BadgeCheck, t: "RM in 4 hours" },
              ].map(({ i: I, t }) => (
                <span key={t} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[13px] backdrop-blur">
                  <I size={14} className="text-[#F9A61C]"/> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Testimonial card */}
          <div className="rc-glass p-4 max-w-sm rc-rise" style={{ animationDelay: "520ms" }}>
            <div className="flex items-center gap-1 text-[#F9A61C] mb-1">
              {Array.from({length: 5}).map((_, i) => <Star key={i} size={14} className="fill-current"/>)}
            </div>
            <div className="text-[14px] text-white/90 leading-relaxed italic">
              &ldquo;Finally, a developer that respects our time. I see every payment, every milestone, every site photo — no calls needed.&rdquo;
            </div>
            <div className="mt-2 text-[12px] text-white/60">— Karthik R., owner · Radiance Solitaire, Chennai</div>
          </div>
        </div>
      </div>

      {/* RIGHT: Form panel */}
      <div className="relative md:w-[42%] flex items-center justify-center p-5 md:p-10 z-10 bg-white text-black">
        <div className="w-full max-w-md rc-rise" style={{ animationDelay: "180ms" }}>
          <div className="rc-glass-light p-7 md:p-9" data-testid="login-panel">
            {mode === "choose" && (
              <>
                <div className="rc-eyebrow mb-3">Sign in to continue</div>
                <div className="rc-title-1">Welcome back.</div>
                <div className="rc-callout mt-2 mb-7">Choose how you&apos;d like to access Radiance Connect.</div>

                <button onClick={() => setMode("buyer")} className="rc-btn rc-btn-orange w-full rc-halo group" data-testid="choose-buyer">
                  <Phone size={18}/> I&apos;m a buyer · OTP login
                  <ArrowRight size={16} className="ml-1 transition-transform group-hover:translate-x-1"/>
                </button>

                <div className="my-4 flex items-center gap-3">
                  <div className="h-px flex-1 bg-[var(--rc-line)]"/>
                  <div className="rc-caption">or</div>
                  <div className="h-px flex-1 bg-[var(--rc-line)]"/>
                </div>

                <button onClick={() => setMode("staff")} className="rc-btn rc-btn-secondary w-full" data-testid="choose-staff">
                  <Mail size={18}/> I&apos;m staff · Email &amp; password
                </button>

                <div className="rc-footnote mt-8 p-4 rounded-xl bg-[var(--rc-near-white)]">
                  <div className="rc-headline" style={{ fontSize: 13, fontWeight: 600 }}>Demo access</div>
                  <div className="mt-1 text-[12.5px] text-[var(--rc-label-secondary)]">
                    Buyer mobile <b className="text-black">9876543210</b> · OTP <b className="text-black">1234</b>
                  </div>
                </div>
              </>
            )}

            {mode === "buyer" && (
              <>
                <button onClick={() => { setMode("choose"); setOtpSent(false); setOtp(["","","",""]); }} className="rc-btn rc-btn-ghost rc-btn-sm mb-2 -ml-3"><ChevronLeft size={16}/> Back</button>
                <div className="rc-eyebrow mb-2">Buyer access</div>
                <div className="rc-title-1">Welcome home.</div>
                <div className="rc-callout mt-2 mb-6">{otpSent ? "Enter the 4-digit code we sent you." : "Sign in with your registered mobile number."}</div>

                {!otpSent ? (
                  <>
                    <label className="rc-footnote mb-1 block">Mobile number</label>
                    <div className="relative">
                      <span className="absolute top-1/2 -translate-y-1/2 left-4 text-[var(--rc-label-secondary)] rc-tabular pointer-events-none" style={{ fontSize: 17 }}>+91</span>
                      <input className="rc-input rc-tabular" style={{ paddingLeft: 60 }} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" data-testid="buyer-mobile" inputMode="numeric"/>
                    </div>
                    <button onClick={requestOtp} disabled={busy || mobile.length < 10} className="rc-btn rc-btn-orange w-full mt-5" data-testid="send-otp-btn">
                      {busy ? <Loader2 className="animate-spin" size={18}/> : <>Send OTP <ArrowRight size={16}/></>}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex gap-3 justify-center my-2">
                      {otp.map((v, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpRefs.current[i] = el)}
                          value={v}
                          onChange={(e) => setOtpAt(i, e.target.value)}
                          onKeyDown={(e) => onOtpKey(i, e)}
                          inputMode="numeric"
                          maxLength={1}
                          className="rc-input !p-0 text-center !w-14 !h-16 !text-[28px] !font-bold rc-tabular"
                          data-testid={`otp-${i}`}
                        />
                      ))}
                    </div>
                    <button onClick={verifyOtp} disabled={busy || otp.join("").length !== 4} className="rc-btn rc-btn-primary w-full mt-4" data-testid="verify-otp-btn">
                      {busy ? <Loader2 className="animate-spin" size={18}/> : <>Verify &amp; Continue <ArrowRight size={16}/></>}
                    </button>
                    <button onClick={requestOtp} disabled={busy} className="rc-btn rc-btn-ghost w-full mt-1 rc-btn-sm">Resend code</button>
                    <div className="rc-footnote mt-4 text-center">Demo OTP <b className="text-black">1234</b></div>
                  </>
                )}
              </>
            )}

            {mode === "staff" && (
              <>
                <button onClick={() => setMode("choose")} className="rc-btn rc-btn-ghost rc-btn-sm mb-2 -ml-3"><ChevronLeft size={16}/> Back</button>
                <div className="rc-eyebrow mb-2">Staff portal</div>
                <div className="rc-title-1">Sign in.</div>
                <div className="rc-callout mt-2 mb-6">For CRM executives, managers &amp; management.</div>

                <label className="rc-footnote mb-1 block">Email</label>
                <input className="rc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="staff-email" autoComplete="username"/>
                <label className="rc-footnote mb-1 mt-4 block">Password</label>
                <input className="rc-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="staff-password" autoComplete="current-password" onKeyDown={(e) => e.key === "Enter" && staffLogin()}/>

                <button onClick={staffLogin} disabled={busy} className="rc-btn rc-btn-primary w-full mt-6" data-testid="staff-login-btn">
                  {busy ? <Loader2 className="animate-spin" size={18}/> : <>Sign in <ArrowRight size={16}/></>}
                </button>

                <div className="mt-6 grid grid-cols-1 gap-2">
                  <div className="rc-footnote mb-1">Quick demo logins:</div>
                  {[
                    ["CRM Executive", "crm@radiancerealty.in"],
                    ["CRM Manager",   "manager@radiancerealty.in"],
                    ["Management",    "karthiga@radiancerealty.in"],
                  ].map(([label, em]) => (
                    <button key={em} onClick={() => fillStaffPreset(em)} className="text-left text-[13px] flex items-center justify-between px-3 py-2.5 rounded-xl bg-[var(--rc-near-white)] hover:bg-[#E8E8EE] transition-colors" data-testid={`preset-${em}`}>
                      <span><b>{label}</b> · {em}</span>
                      <ArrowRight size={14} className="text-[var(--rc-label-secondary)]"/>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rc-footnote mt-4 text-center text-[var(--rc-label-secondary)]">
            <span className="rc-gradient-text font-semibold">Trust in every brick.</span> · Clarity in every step.
          </div>
        </div>
      </div>
    </div>
  );
}
